import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './UgcPage.css';
import UniversalLayout from './components/UniversalLayout';
import { useAuthContext } from './components/AuthProvider';
import AuthModal from './components/AuthModal';
import AgentAutoReloadModal from './components/AgentAutoReloadModal';
import { initializeProject, startAgentCompose, getAgentEligibility, generateUgcIdeas } from './lib/api';

// Default characters — image + audio pairs.
const DEFAULT_CHARACTERS = [
  { id: 'julia', name: 'Julia', thumb: 'https://latted.b-cdn.net/misc/Julia.png', imageUrl: 'https://latted.b-cdn.net/misc/Julia.png' },
  { id: 'jess', name: 'Jess', thumb: 'https://latted.b-cdn.net/misc/Jess.png', imageUrl: 'https://latted.b-cdn.net/misc/Jess.png' },
  { id: 'jake', name: 'Jake', thumb: 'https://latted.b-cdn.net/misc/Jake.png', imageUrl: 'https://latted.b-cdn.net/misc/Jake.png' },
  { id: 'kim', name: 'Kim', thumb: 'https://latted.b-cdn.net/misc/Kim.png', imageUrl: 'https://latted.b-cdn.net/misc/Kim.png' },
  { id: 'roy', name: 'Roy', thumb: 'https://latted.b-cdn.net/misc/Roy.png', imageUrl: 'https://latted.b-cdn.net/misc/Roy.png' },
  { id: 'tate', name: 'Tate', thumb: 'https://latted.b-cdn.net/misc/Tate.png', imageUrl: 'https://latted.b-cdn.net/misc/Tate.png' },
  { id: 'clav', name: 'Clav', thumb: 'https://latted.b-cdn.net/misc/Clav.png', imageUrl: 'https://latted.b-cdn.net/misc/Clav.png' },
  { id: 'ashley', name: 'Ashley', thumb: 'https://latted.b-cdn.net/misc/Ashley.png', imageUrl: 'https://latted.b-cdn.net/misc/Ashley.png' },
];

const UGC_DEFAULT_PROMPT =
  'Create a UGC-style product ad video. Use the ugc-video skill. ' +
  'The character and product media are attached. Script:\n\n';

function UgcPage() {
  const navigate = useNavigate();
  const {
    user, loading, openAuthModal, closeAuthModal, authModalOpen,
    signInWithGoogle, signInWithEmail,
  } = useAuthContext();

  // ── Page phase ──
  // 'input' | 'generating_ideas' | 'showing_ideas' | 'generating_videos'
  const [pagePhase, setPagePhase] = useState('input');

  // ── Character state ──
  const [selectedDefault, setSelectedDefault] = useState(null);
  const [characterFile, setCharacterFile] = useState(null);
  const [characterPreviewUrl, setCharacterPreviewUrl] = useState(null);
  const characterInputRef = useRef(null);

  // ── Product state ──
  const [productFiles, setProductFiles] = useState([]);
  const [productPreviews, setProductPreviews] = useState([]);

  // ── Product Description (replaces Prompt) ──
  const [description, setDescription] = useState('');
  const [numIdeas, setNumIdeas] = useState(3);

  // ── Ideas from LLM ──
  const [ideas, setIdeas] = useState([]); // [{ title, detailed_script, selected, expanded }]

  // ── Captions toggle ──
  const [addCaptions, setAddCaptions] = useState(false);

  // ── Custom script ──
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customScript, setCustomScript] = useState('');

  // ── Generation tracking ──
  const [generatingProjects, setGeneratingProjects] = useState({}); // { [index]: { status, projectId, error } }

  // ── Submit / errors ──
  const [submitError, setSubmitError] = useState(null);
  const pendingSubmitRef = useRef(null); // 'generate' | null

  // ── Auto-reload modal ──
  const [showAutoReloadModal, setShowAutoReloadModal] = useState(false);
  const [eligibility, setEligibility] = useState(null);

  // ── Character handlers ──
  const handleCharacterUploadClick = () => characterInputRef.current?.click();

  const handleCharacterFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (characterPreviewUrl) URL.revokeObjectURL(characterPreviewUrl);
    setCharacterFile(file);
    setCharacterPreviewUrl(URL.createObjectURL(file));
    setSelectedDefault(null);
    e.target.value = '';
  };

  const handleSelectDefault = (char) => {
    if (characterPreviewUrl) URL.revokeObjectURL(characterPreviewUrl);
    setCharacterFile(null);
    setCharacterPreviewUrl(null);
    setSelectedDefault(char);
  };

  const handleRemoveCharacter = () => {
    if (characterPreviewUrl) URL.revokeObjectURL(characterPreviewUrl);
    setCharacterFile(null);
    setCharacterPreviewUrl(null);
    setSelectedDefault(null);
  };

  // ── Product handlers ──
  const productInputRef = useRef(null);

  const handleProductUploadClick = () => productInputRef.current?.click();

  const handleProductFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newFiles = [...productFiles, ...files];
    const newPreviews = [
      ...productPreviews,
      ...files.map((f) => ({
        url: URL.createObjectURL(f),
        type: 'image',
      })),
    ];
    setProductFiles(newFiles);
    setProductPreviews(newPreviews);
    e.target.value = '';
  };

  const handleRemoveProduct = (idx) => {
    URL.revokeObjectURL(productPreviews[idx].url);
    setProductFiles((prev) => prev.filter((_, i) => i !== idx));
    setProductPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Derived ──
  const hasCharacter = !!(characterFile || selectedDefault);
  const canGenerateIdeas =
    hasCharacter &&
    productFiles.length > 0 &&
    description.trim().length > 0 &&
    pagePhase === 'input';

  // ── Generate Ideas ──
  const handleGenerateIdeas = useCallback(async () => {
    setPagePhase('generating_ideas');
    setSubmitError(null);

    try {
      const result = await generateUgcIdeas({
        description: description.trim(),
        characterImageUrl: selectedDefault?.imageUrl || null,
        characterName: selectedDefault?.name || null,
        characterFile: characterFile || null,
        productFiles,
        numIdeas,
      });

      setIdeas(result.ideas.map((idea) => ({ ...idea, selected: false, expanded: false })));
      setPagePhase('showing_ideas');
    } catch (err) {
      console.error('Failed to generate ideas:', err);
      setSubmitError('Failed to generate ideas. Please try again.');
      setPagePhase('input');
    }
  }, [description, selectedDefault, characterFile, productFiles, numIdeas]);

  // ── Idea selection ──
  const isMultiSelect = eligibility?.auto_reload_enabled === true;

  const handleToggleIdea = (idx) => {
    setIdeas((prev) => prev.map((idea, i) => {
      if (i === idx) return { ...idea, selected: !idea.selected };
      // In single-select mode, deselect others
      if (!isMultiSelect) return { ...idea, selected: false };
      return idea;
    }));
  };

  const handleToggleExpand = (idx) => {
    setIdeas((prev) => prev.map((idea, i) =>
      i === idx ? { ...idea, expanded: !idea.expanded } : idea
    ));
  };

  // ── Add custom script as an idea ──
  const handleAddCustomScript = () => {
    if (!customTitle.trim() || !customScript.trim()) return;
    setIdeas((prev) => [
      ...prev,
      { title: customTitle.trim(), detailed_script: customScript.trim(), selected: false, expanded: false },
    ]);
    setCustomTitle('');
    setCustomScript('');
    setShowCustomForm(false);
  };

  // ── Single project generation (navigate away) ──
  const generateSingleProjectAndNavigate = useCallback(async (idea) => {
    const projectId = Math.random().toString(36).substring(2, 15);
    const captionSuffix = addCaptions ? '\n\nAdd captions to all videos with speech.' : '';
    const fullPrompt = UGC_DEFAULT_PROMPT + idea.detailed_script + captionSuffix;
    const files = characterFile ? [characterFile, ...productFiles] : [...productFiles];
    const attachmentUrls = selectedDefault ? [selectedDefault.imageUrl] : [];

    await initializeProject(projectId, fullPrompt, '9:16');
    await startAgentCompose(projectId, fullPrompt, files, 'pro', attachmentUrls, 'ugc_ads_template');

    navigate(`/project/${projectId}`, {
      state: { agentMode: true, promptText: fullPrompt },
    });
  }, [characterFile, selectedDefault, productFiles, navigate, addCaptions]);

  // ── Multi-project generation (stay on page) ──
  const generateSingleProject = useCallback(async (idea, idx) => {
    const projectId = Math.random().toString(36).substring(2, 15);
    setGeneratingProjects((prev) => ({ ...prev, [idx]: { status: 'initializing', projectId } }));

    const captionSuffix = addCaptions ? '\n\nAdd captions to all videos with speech.' : '';
    const fullPrompt = UGC_DEFAULT_PROMPT + idea.detailed_script + captionSuffix;
    const files = characterFile ? [characterFile, ...productFiles] : [...productFiles];
    const attachmentUrls = selectedDefault ? [selectedDefault.imageUrl] : [];

    try {
      await initializeProject(projectId, fullPrompt, '9:16');
      setGeneratingProjects((prev) => ({ ...prev, [idx]: { status: 'composing', projectId } }));

      await startAgentCompose(projectId, fullPrompt, files, 'pro', attachmentUrls, 'ugc_ads_template');
      setGeneratingProjects((prev) => ({ ...prev, [idx]: { status: 'done', projectId } }));
    } catch (err) {
      console.error(`Project generation failed for idea ${idx}:`, err);
      setGeneratingProjects((prev) => ({ ...prev, [idx]: { status: 'error', projectId, error: 'Video generation could not be completed. Please try again.' } }));
    }
  }, [characterFile, selectedDefault, productFiles, addCaptions]);

  // ── Main generate handler ──
  const handleGenerate = useCallback(async () => {
    const selectedIdeas = ideas
      .map((idea, idx) => ({ ...idea, idx }))
      .filter((idea) => idea.selected);

    if (selectedIdeas.length === 0) return;

    // Auth check
    if (!user && !loading) {
      pendingSubmitRef.current = 'generate';
      openAuthModal();
      return;
    }

    // Eligibility check
    let elig;
    try {
      elig = await getAgentEligibility();
      setEligibility(elig);
    } catch (err) {
      console.error('Eligibility check failed:', err);
    }

    // Handle auto_reload_required
    if (elig && !elig.allowed && elig.reason === 'auto_reload_required') {
      setShowAutoReloadModal(true);
      return;
    }

    const canMulti = elig?.auto_reload_enabled === true;

    if (canMulti) {
      // Path A: parallel generation, stay on page
      setPagePhase('generating_videos');
      await Promise.allSettled(
        selectedIdeas.map((idea) => generateSingleProject(idea, idea.idx))
      );
    } else {
      // Path B: single project, navigate away
      setPagePhase('generating_videos');
      try {
        await generateSingleProjectAndNavigate(selectedIdeas[0]);
      } catch (err) {
        console.error('Generation failed:', err);
        setSubmitError('Failed to start generation. Please try again.');
        setPagePhase('showing_ideas');
      }
    }
  }, [ideas, user, loading, openAuthModal, generateSingleProject, generateSingleProjectAndNavigate]);

  // ── Auto-reload modal handler ──
  const handleEnableAutoReloadAndContinue = useCallback(async () => {
    setShowAutoReloadModal(false);
    // After enabling auto-reload, retry generation
    handleGenerate();
  }, [handleGenerate]);

  // ── Resume after auth ──
  useEffect(() => {
    if (user && pendingSubmitRef.current) {
      const action = pendingSubmitRef.current;
      pendingSubmitRef.current = null;

      if (action === 'generate') {
        handleGenerate();
      }
    }
  }, [user, handleGenerate]);

  // ── Scroll to top when ideas are ready ──
  useEffect(() => {
    if (pagePhase === 'showing_ideas') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pagePhase]);

  // ── Back to edit ──
  const handleBackToEdit = () => {
    setPagePhase('input');
    setIdeas([]);
    setGeneratingProjects({});
    setSubmitError(null);
  };

  const selectedCount = ideas.filter((i) => i.selected).length;

  return (
    <UniversalLayout>
      <div className="ugc-content">
        <main className="ugc-main">
          <h1 className="ugc-title">
            Create <span className="ugc-title-em">UGC</span> Ads
          </h1>
          <p className="ugc-subtitle">
            Pick a creator, add your product, describe it — AI generates
            scroll-stopping video ideas for you.
          </p>

          {/* ── Input Sections (visible in input & generating_ideas phases) ── */}
          {(pagePhase === 'input' || pagePhase === 'generating_ideas') && (
            <>
              {/* ── Section 1: Character ── */}
              <div className="ugc-section">
                <div className="ugc-section-header">
                  <span className="ugc-section-number">01</span>
                  <div className="ugc-section-label">
                    <span className="ugc-section-title">Character</span>
                    <span className="ugc-section-desc">
                      Upload an image or pick a default creator
                    </span>
                  </div>
                </div>

                <div className="ugc-section-body">
                  {hasCharacter ? (
                    <div className="ugc-preview-box">
                      {characterPreviewUrl ? (
                        <img
                          src={characterPreviewUrl}
                          alt="Character"
                          className="ugc-preview-media"
                        />
                      ) : selectedDefault ? (
                        <div className="ugc-default-selected">
                          <img
                            src={selectedDefault.thumb}
                            alt={selectedDefault.name}
                            className="ugc-preview-media"
                          />
                          <span className="ugc-default-selected-name">
                            {selectedDefault.name}
                          </span>
                        </div>
                      ) : null}
                      <button
                        className="ugc-btn-remove"
                        onClick={handleRemoveCharacter}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="ugc-upload-zone">
                      <input
                        ref={characterInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        style={{ display: 'none' }}
                        onChange={handleCharacterFileChange}
                      />
                      <button
                        className="ugc-btn-primary"
                        onClick={handleCharacterUploadClick}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        Upload Character Image
                      </button>
                      <span className="ugc-upload-hint">PNG, JPG, WebP</span>
                    </div>
                  )}

                  {DEFAULT_CHARACTERS.length > 0 && !hasCharacter && (
                    <>
                      <div className="ugc-or-divider">
                        <span>or pick a default</span>
                      </div>
                      <div className="ugc-defaults-grid">
                        {DEFAULT_CHARACTERS.map((char) => (
                          <button
                            key={char.id}
                            className={`ugc-default-card${
                              selectedDefault?.id === char.id
                                ? ' ugc-default-card--selected'
                                : ''
                            }`}
                            onClick={() => handleSelectDefault(char)}
                          >
                            <img
                              src={char.thumb}
                              alt={char.name}
                              className="ugc-default-thumb"
                            />
                            <span className="ugc-default-name">{char.name}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* ── Section 2: Product ── */}
              <div className="ugc-section">
                <div className="ugc-section-header">
                  <span className="ugc-section-number">02</span>
                  <div className="ugc-section-label">
                    <span className="ugc-section-title">Product</span>
                    <span className="ugc-section-desc">
                      Upload your logo, product photos and/or screenshots
                    </span>
                  </div>
                </div>

                <div className="ugc-section-body">
                  {productPreviews.length > 0 && (
                    <div className="ugc-product-grid">
                      {productPreviews.map((p, i) => (
                        <div key={i} className="ugc-product-thumb-wrap">
                          <img src={p.url} alt="" className="ugc-product-thumb" />
                          <button
                            className="ugc-product-thumb-remove"
                            onClick={() => handleRemoveProduct(i)}
                            aria-label="Remove"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    ref={productInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleProductFileChange}
                  />
                  <button
                    className="ugc-btn-secondary"
                    onClick={handleProductUploadClick}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    {productFiles.length > 0 ? 'Add More' : 'Upload Product Images'}
                  </button>
                  <span className="ugc-upload-hint">
                    PNG, JPG, WebP
                  </span>
                </div>
              </div>

              {/* ── Section 3: Product Description ── */}
              <div className="ugc-section">
                <div className="ugc-section-header">
                  <span className="ugc-section-number">03</span>
                  <div className="ugc-section-label">
                    <span className="ugc-section-title">Product Description</span>
                    <span className="ugc-section-desc">
                      Describe your product and what makes it go viral
                    </span>
                  </div>
                </div>

                <div className="ugc-section-body">
                  <textarea
                    className="ugc-prompt-input"
                    placeholder='e.g. "Cal AI is an AI-powered calorie tracking app. Users take a photo of their food and it instantly shows calories and macros. The viral hook is how easy it makes calorie counting — no manual logging, just snap and track."'
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                  />
                  <div className="ugc-num-ideas-row">
                    <label className="ugc-num-ideas-label" htmlFor="ugc-num-ideas">
                      Number of video ideas
                    </label>
                    <input
                      id="ugc-num-ideas"
                      type="number"
                      className="ugc-num-ideas-input"
                      min={1}
                      max={10}
                      value={numIdeas}
                      onChange={(e) => setNumIdeas(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                    />
                  </div>
                  <div className="ugc-captions-row">
                    <label className="ugc-captions-label" htmlFor="ugc-captions">
                      <input
                        id="ugc-captions"
                        type="checkbox"
                        checked={addCaptions}
                        onChange={(e) => setAddCaptions(e.target.checked)}
                      />
                      Add captions to videos
                    </label>
                  </div>
                </div>
              </div>

              {/* ── Submit ── */}
              <div className="ugc-submit-area">
                <button
                  className="ugc-btn-submit"
                  disabled={!canGenerateIdeas || pagePhase === 'generating_ideas'}
                  onClick={handleGenerateIdeas}
                >
                  {pagePhase === 'generating_ideas' ? (
                    <>
                      <span className="ugc-btn-spinner" />
                      Generating ideas…
                    </>
                  ) : (
                    'Generate Video Ideas'
                  )}
                </button>
                {submitError && (
                  <span className="ugc-submit-error">{submitError}</span>
                )}
              </div>
            </>
          )}

          {/* ── Ideas Phase ── */}
          {(pagePhase === 'showing_ideas' || pagePhase === 'generating_videos') && (
            <div className="ugc-ideas-section">
              <div className="ugc-ideas-header">
                <h2 className="ugc-ideas-title">
                  Your Video Ideas
                  <span className="ugc-ideas-count">{ideas.length}</span>
                </h2>
                {pagePhase === 'showing_ideas' && (
                  <button className="ugc-btn-back" onClick={handleBackToEdit}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                    Back to Edit
                  </button>
                )}
              </div>

              {!isMultiSelect && pagePhase === 'showing_ideas' && (
                <p className="ugc-ideas-hint">
                  Select one idea to generate. Enable auto-reload credits to generate multiple at once.
                </p>
              )}
              {isMultiSelect && pagePhase === 'showing_ideas' && (
                <p className="ugc-ideas-hint">
                  Select one or more ideas to generate in parallel.
                </p>
              )}

              <div className="ugc-ideas-list">
                {ideas.map((idea, idx) => {
                  const proj = generatingProjects[idx];
                  return (
                    <div
                      key={idx}
                      className={`ugc-idea-card${idea.selected ? ' ugc-idea-card--selected' : ''}${proj ? ' ugc-idea-card--generating' : ''}`}
                    >
                      <div className="ugc-idea-card-header">
                        {pagePhase === 'showing_ideas' && (
                          <button
                            className={`ugc-idea-select${idea.selected ? ' ugc-idea-select--active' : ''}`}
                            onClick={() => handleToggleIdea(idx)}
                            aria-label={idea.selected ? 'Deselect' : 'Select'}
                          >
                            {idea.selected && (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </button>
                        )}
                        <div className="ugc-idea-title-wrap">
                          <span className="ugc-idea-title">{idea.title}</span>
                        </div>
                        {proj && (
                          <div className="ugc-idea-status">
                            {(proj.status === 'initializing' || proj.status === 'composing') && (
                              <span className="ugc-idea-spinner" />
                            )}
                            {proj.status === 'done' && (
                              <button
                                className="ugc-btn-go-to-project"
                                onClick={() => navigate(`/project/${proj.projectId}`)}
                              >
                                Go to Project
                              </button>
                            )}
                            {proj.status === 'error' && (
                              <span className="ugc-idea-error">Failed</span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="ugc-idea-preview">
                        {idea.expanded
                          ? idea.detailed_script
                          : idea.detailed_script.slice(0, 200) + (idea.detailed_script.length > 200 ? '…' : '')}
                      </div>

                      <button
                        className="ugc-idea-expand-btn"
                        onClick={() => handleToggleExpand(idx)}
                      >
                        {idea.expanded ? 'Show less' : 'Show full script'}
                      </button>
                    </div>
                  );
                })}

                {/* ── Custom Script Card ── */}
                {pagePhase === 'showing_ideas' && (
                  <div className="ugc-idea-card ugc-custom-script-card">
                    {!showCustomForm ? (
                      <button
                        className="ugc-custom-script-btn"
                        onClick={() => setShowCustomForm(true)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Write a Custom Script
                      </button>
                    ) : (
                      <div className="ugc-custom-form">
                        <input
                          type="text"
                          className="ugc-custom-title-input"
                          placeholder="Video title"
                          value={customTitle}
                          onChange={(e) => setCustomTitle(e.target.value)}
                        />
                        <textarea
                          className="ugc-prompt-input"
                          placeholder="Write your detailed script here..."
                          value={customScript}
                          onChange={(e) => setCustomScript(e.target.value)}
                          rows={6}
                        />
                        <div className="ugc-custom-form-actions">
                          <button
                            className="ugc-btn-secondary"
                            onClick={() => { setShowCustomForm(false); setCustomTitle(''); setCustomScript(''); }}
                          >
                            Cancel
                          </button>
                          <button
                            className="ugc-btn-primary"
                            onClick={handleAddCustomScript}
                            disabled={!customTitle.trim() || !customScript.trim()}
                          >
                            Add Script
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Generate Selected Button ── */}
              {pagePhase === 'showing_ideas' && (
                <div className="ugc-submit-area">
                  <button
                    className="ugc-btn-submit"
                    disabled={selectedCount === 0}
                    onClick={handleGenerate}
                  >
                    {selectedCount === 0
                      ? 'Select ideas to generate'
                      : selectedCount === 1
                        ? 'Generate 1 Video'
                        : `Generate ${selectedCount} Videos`}
                  </button>
                  {submitError && (
                    <span className="ugc-submit-error">{submitError}</span>
                  )}
                </div>
              )}

              {/* ── During generation ── */}
              {pagePhase === 'generating_videos' && (
                <div className="ugc-submit-area">
                  <p className="ugc-generating-label">
                    <span className="ugc-btn-spinner" />
                    Generating videos…
                  </p>
                </div>
              )}
            </div>
          )}
        </main>

        {/* ── How it Works ── */}
        {pagePhase === 'input' && (
          <section className="ugc-how">
            <h2 className="ugc-how-title">
              How it <em>works</em>
            </h2>
            <div className="ugc-steps">
              <div className="ugc-step">
                <div className="ugc-step-number">01</div>
                <div className="ugc-step-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <h3 className="ugc-step-title">Pick a Creator</h3>
                <p className="ugc-step-desc">
                  Upload a character image or choose from our defaults.
                </p>
              </div>
              <div className="ugc-step">
                <div className="ugc-step-number">02</div>
                <div className="ugc-step-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                    <path d="M16 3l-4 4-4-4" />
                  </svg>
                </div>
                <h3 className="ugc-step-title">Describe Product</h3>
                <p className="ugc-step-desc">
                  Upload product photos and describe what makes it special.
                </p>
              </div>
              <div className="ugc-step">
                <div className="ugc-step-number">03</div>
                <div className="ugc-step-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </div>
                <h3 className="ugc-step-title">Get Your Ads</h3>
                <p className="ugc-step-desc">
                  AI generates video ideas — pick your favorites and
                  we produce them automatically.
                </p>
              </div>
            </div>
          </section>
        )}

        <footer className="ugc-footer">Powered by Latted</footer>
      </div>

      <AuthModal
        isOpen={!loading && !user && authModalOpen}
        onClose={closeAuthModal}
        onSignIn={() => signInWithGoogle()}
        onEmailSignIn={signInWithEmail}
      />

      <AgentAutoReloadModal
        isOpen={showAutoReloadModal}
        onClose={() => {
          setShowAutoReloadModal(false);
        }}
        onEnableAutoReload={handleEnableAutoReloadAndContinue}
        onCreateBlankProject={() => {
          setShowAutoReloadModal(false);
          // Fall back to single generation for first selected idea
          const firstSelected = ideas.find((i) => i.selected);
          if (firstSelected) {
            generateSingleProjectAndNavigate(firstSelected).catch((err) => {
              console.error('Fallback generation failed:', err);
              setSubmitError('Failed to start generation.');
            });
          }
        }}
      />
    </UniversalLayout>
  );
}

export default UgcPage;
